import { wmsLog } from "@/api/delivery/useDeliveryQuery";
import { ItemsDetailForm } from "@/features/item/components/form/ItemsDetailForm";
import { LocationDetailForm } from "@/features/location/detail/LocationDetailForm";
import { useModal } from "@/features/modal/ModalStackManager";
import { LinkAnchor } from "@/features/standardLayout/LinkAnchor";
import { StockPlanDetailForm } from "@/features/stockPlan/form/DetailForm";
import { TransportationDetailForm } from "@/features/transportation/form/TransportationDetailForm";
import { DetailLink } from "@/features/ui/detail/DetailLink";
import { MantineReactCommonTable } from "@/features/ui/mantineTable";
import { UsersDetailForm } from "@/features/users/components/form/UsersDetailForm";
import timeUtil from "@/utils/timeUtil";
import { WmsLogsGet200ResponseRowsInner } from "@sizlcorp/sizl-api-document/dist/models";
import { useQuery } from "@tanstack/react-query";
import {
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
} from "mantine-react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
interface DeliveredFormProps {
  purchaseOrderItemID: number | undefined;
}

export const DeliveredForm = (params: DeliveredFormProps) => {
  const { purchaseOrderItemID } = params;
  const { openModal } = useModal();
  const { t } = useTranslation();

  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 1,
    pageSize: 10,
  });
  // 각 컬럼 sort
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  // 각 컬럼 filter
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
  // 전체 검색 (단, serchFields에 필드를 직접 입력해줘야함.)
  const [globalFilter, setGlobalFilter] = useState("");

  // sort를 id 별 string으로 만들어줌
  const sortString = sorting.map(({ id, desc }) => (desc ? `-${id}` : id)).join(",");

  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

  // filter를 id 별 like를 사용하여 단어가 포함되어있는지 확인
  const filterQuery: { [key: string]: { $iLike: string } } = columnFilters.reduce<{
    [key: string]: { $iLike: string };
  }>((acc, filter) => {
    acc[filter.id] = { $iLike: `%${filter.value}%` as string };
    return acc;
  }, {});

  const { data } = useQuery(
    wmsLog.get({
      query: [{ purchaseOrderItemId: purchaseOrderItemID }],
      populate: ["locationName", "lot", "item", "transportation", "stockPlan"],
      pageSize: pagination.pageSize,
      page: pagination.pageIndex,
      search: globalFilter,
      sort: sortString || undefined,
    }),
  );

  const rows: readonly WmsLogsGet200ResponseRowsInner[] = data?.data.rows ?? [];

  const columns = useMemo<MRT_ColumnDef<WmsLogsGet200ResponseRowsInner>[]>(
    () => [
      {
        accessorKey: "id",
        header: t("delivery.id"),
        size: 50,
      },
      {
        accessorKey: "createdAt",
        header: t("delivery.deadline"),
        Cell: ({ cell }) => timeUtil(cell.getValue<string>()),
      },
      {
        accessorFn: (row) => row.actionParam?.params?.itemCode,
        header: t("item.code"),
        Cell: ({ cell }) => (
          <LinkAnchor
            onClick={() => {
              openModal(
                <ItemsDetailForm itemCode={cell.getValue<string>()} />,
                null,
                t("common.detail", { item: t("item.item") }),
              );
            }}
          >
            {cell.getValue<string>()}
          </LinkAnchor>
        ),
      },
      {
        accessorFn: (row) => row.actionParam?.params?.locationCode,
        header: t("location.code"),
        Cell: ({ cell }) => (
          <DetailLink
            onClick={() => {
              openModal(
                <LinkAnchor
                  onClick={() =>
                    openModal(
                      <LocationDetailForm locationCode={cell.getValue<string>()} />,
                      null,
                      t("common.detail", { item: t("location.location") }),
                    )
                  }
                >
                  {cell.getValue<string>()}
                </LinkAnchor>,
                null,
                t("common.detail", { item: t("location.location") }),
              );
            }}
          >
            {cell.getValue<string>()}
          </DetailLink>
        ),
      },
      {
        accessorFn: (row) => row.actionParam?.params?.lotInfo?.name,
        header: t("lot.name"),
      },
      {
        accessorFn: (row) => row.actionParam?.params?.quantity,
        header: t("inventory.quantity"),
      },
      {
        accessorFn: (row) => row.transportation?.vehicleCode,
        header: t("vehicle.code"),
      },
      {
        accessorFn: (row) => row.transportation?.code,
        header: t("transportation.code"),
        Cell: ({ row }) => {
          return (
            <LinkAnchor
              onClick={() => {
                openModal(
                  <TransportationDetailForm transportationID={row.original.transportationId} />,
                  null,
                  t("common.detail", { item: t("transportation.transportation") }),
                );
              }}
            >
              {row.original?.transportation?.code}
            </LinkAnchor>
          );
        },
      },
      {
        accessorFn: (row) => row.stockPlan?.code,
        header: t("stockPlan.code"),
        Cell: ({ row }) => {
          return (
            <LinkAnchor
              onClick={() => {
                openModal(
                  <StockPlanDetailForm stockPlanId={row.original.stockPlanId} />,
                  null,
                  t("common.detail", { item: t("stockPlan.stockPlan") }),
                );
              }}
            >
              {row.original?.stockPlan?.code}
            </LinkAnchor>
          );
        },
      },
      {
        accessorFn: (row) => row.creatorUserCode,
        header: t("order.manager"),
        Cell: ({ cell }) => (
          <DetailLink
            onClick={() => {
              openModal(<UsersDetailForm UserCode={cell.getValue<string>()} />, null, "");
            }}
          >
            {cell.getValue<string>()}
          </DetailLink>
        ),
      },
    ],
    [t],
  );
  return (
    <MantineReactCommonTable
      data={rows as WmsLogsGet200ResponseRowsInner[]}
      columns={columns}
      totalCount={data?.data.total}
      totalPages={data?.data.totalPages}
      pagination={pagination} // pagination
      setPagination={setPagination}
      sorting={sorting} // 정렬
      setSorting={setSorting}
      columnFilters={columnFilters} // 컬럼별 검색
      setColumnFilters={setColumnFilters}
      globalFilter={globalFilter} // 전체 검색
      setGlobalFilter={setGlobalFilter}
      rowSelection={rowSelection} // 선택
      setRowSelection={setRowSelection}
      selectedRowIds={selectedRowIds} // 선택된 id
      setSelectedRowIds={setSelectedRowIds}
      enableTopToolbar={true}
    />
  );
};
